import { Slider, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../../../context/AlertContext";
import { useVideo } from "../../../../../context/juno/VideoContext";
import { ColumnBox } from "../../../../../utils/styledBox";
import { useVideoMetadata } from "../../../../../hooks/useVideoMetadata";
import ModelVersionSelectBox from "../ModelVersionSelectBox";
import VideoUpload from "../VideoUpload";

const ParamsREALESRGAN = () => {
  const { t } = useTranslation();
  const { scale, setScale, video1, setVideo1, updateParams, setVideo1Metadata } = useVideo();
  const { setAlert } = useAlert();
  const { extractMetadataFromBase64, validateMetadata } = useVideoMetadata();

  const [maxScale, setMaxScale] = useState<number>(4);

  // フロントエンドでメタデータの取得
  useEffect(() => {
    if (video1) {
      const processVideoMetadata = async () => {
        try {
          const metadata = await extractMetadataFromBase64(video1);
          setVideo1Metadata(metadata);

          // バリデーション
          try {
            validateMetadata(metadata, scale, 60);

            // 4Kの総ピクセル数を基準に計算
            const maxPixels = 3840 * 2160;
            const inputPixels = metadata.width * metadata.height;
            const calculatedMaxScale = Math.sqrt(maxPixels / inputPixels);
            // 小数点第1位までに制限（4以下）
            const finalMaxScale = Math.min(Math.floor(calculatedMaxScale * 10) / 10, 4);
            setMaxScale(finalMaxScale);

            // スケール値の制限
            if (scale > finalMaxScale) {
              setScale(finalMaxScale);
            }
          } catch (validationError) {
            setVideo1("");
            if (validationError instanceof Error) {
              if (validationError.message.includes("解像度が4K")) {
                setAlert("error", t("juno.video.error.max.resolution"));
              } else if (validationError.message.includes("動画の長さ")) {
                setAlert("error", t("juno.video.error.max.videoDuration", { sec: 60 }));
              } else if (validationError.message.includes("フレーム数")) {
                setAlert("error", t("juno.video.error.max.frames"));
              } else {
                setAlert("error", t("juno.video.error.generate"));
              }
            }
            return;
          }
        } catch (error) {
          console.error(error);
          setVideo1("");
          setAlert("error", t("juno.video.error.generate"));
          return;
        }
      };

      processVideoMetadata();
    } else {
      setVideo1Metadata(null);
      setMaxScale(4);
    }
  }, [video1, extractMetadataFromBase64, validateMetadata, scale]);

  // スライダーのマークを計算
  const marks = useMemo(() => {
    const baseMarks = [
      { value: 1, label: "1x" },
      { value: 2, label: "2x" },
      { value: 3, label: "3x" },
      { value: 4, label: "4x" },
    ];
    return baseMarks.filter((mark) => mark.value <= maxScale);
  }, [maxScale]);

  return (
    <ColumnBox sx={{ gap: 4 }}>
      <ColumnBox>
        {/* Model */}
        <Typography variant="body2" mb={1}>
          {t("juno.video.ai.model")}
        </Typography>
        <ModelVersionSelectBox onChange={(value) => updateParams("REALESRGAN", "modelVersion", value)} />
      </ColumnBox>

      <ColumnBox>
        <Typography variant="body2" mb={1}>
          {t("juno.video.params.video")}
        </Typography>
        <VideoUpload video={video1} setVideo={setVideo1} />
      </ColumnBox>

      <ColumnBox>
        <Typography variant="body2" mb={1}>
          {t("juno.video.params.scale")}
        </Typography>
        <Slider
          value={scale}
          onChange={(_, value) => {
            // ローカルのスケール値を更新
            const newScale = value as number;
            if (newScale > maxScale) {
              setScale(maxScale);
            } else {
              setScale(newScale);
            }
          }}
          min={1}
          max={maxScale}
          step={0.1}
          valueLabelDisplay="auto"
          marks={marks}
        />
      </ColumnBox>
    </ColumnBox>
  );
};

export default ParamsREALESRGAN;
